import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from '../select';
import { Pagination } from '../pagination';
import { useSearchListing } from '../../react-hooks/use-search-listing';
import SearchCard from './card';
import Facets from './facets';
import { scrollToElement } from '../../utils';
import Spinner from '../spinner';

function SearchListing({
  endpoint,
  pageQueryString,
  itemsPerPageQueryString,
  itemsPerPageLabel,
  itemsPerPageOptions,
  sortByLabel,
  sortByOptions,
  sortByQueryString,
  appliedFiltersLabel,
  clearAllLabel,
  queryLabel,
  errorTemplate
}) {
  const { current: sortByData } = useRef(JSON.parse(sortByOptions));
  const { current: itemsPerPageData } = useRef(JSON.parse(itemsPerPageOptions));
  const {
    data,
    error,
    isLoading,
    page,
    setPage,
    sortBy,
    setSortBy,
    itemsPerPage,
    setItemsPerPage,
    activeFacets,
    setActiveFacets,
    isFacetsOpen,
    setIsFacetsOpen
  } = useSearchListing({
    endpoint,
    pageQueryString,
    sortByQueryString,
    itemsPerPageQueryString
  });

  if (error) return null;

  const [needsReload, setNeedsReload] = useState(false);

  useEffect(() => {
    if (needsReload) {
      scrollToElement('.search-listing');
      setNeedsReload(false);
    }
  }, [data]);

  function setSelectValue(func, val) {
    setPage('');
    func(val);
    setNeedsReload(true);
  }

  function handlePageChange(value) {
    setNeedsReload(true);
    setPage(value);
  }

  return (
    <>
      {isLoading ? <Spinner /> : null}
      <div aria-label="Filters" className="search-listing__filters">
        <p className="search-listing__results-label">{queryLabel}</p>
        {data?.Facets || data?.Websites ? (
          <Facets
            appliedFiltersLabel={appliedFiltersLabel}
            setPage={setPage}
            data={data?.Facets}
            websites={data?.Websites}
            isOpen={isFacetsOpen}
            activeFacets={activeFacets}
            setIsOpen={setIsFacetsOpen}
            setActiveFacets={setActiveFacets}
            clearAllLabel={clearAllLabel}
          />
        ) : null}
      </div>
      <div className="search-listing__main">
        <div className="search-listing__header">
          <Select
            className="lg-only"
            label={sortByLabel}
            options={sortByData}
            value={sortBy}
            onChange={(value) => setSelectValue(setSortBy, value)}
          />
          {data?.TotalPages ? (
            <Pagination
              currentPage={page || '1'}
              totalPages={`${data?.TotalPages}`}
              onPageChange={handlePageChange}
            />
          ) : null}
          <Select
            className="lg-only"
            label={itemsPerPageLabel}
            options={itemsPerPageData}
            value={itemsPerPage}
            onChange={(value) => setSelectValue(setItemsPerPage, value)}
          />
        </div>
        <div className="search-listing__list">
          {data?.Items ? (
            data.Items?.map(
              (
                {
                  Author,
                  Type,
                  Title,
                  Link,
                  Thumb,
                  OverlayImage,
                  Description,
                  PublishedDate,
                  PublishedDateText,
                  Breadcumbs,
                  Prices,
                  isLocked,
                  isVideo,
                  ProductImageFlags
                },
                index
              ) => (
                <SearchCard
                  key={index}
                  Author={Author}
                  Type={Type}
                  Title={Title}
                  Link={Link}
                  Thumb={Thumb}
                  OverlayImage={OverlayImage}
                  Description={Description}
                  PublishedDate={PublishedDate}
                  PublishedDateText={PublishedDateText}
                  Breadcumbs={Breadcumbs}
                  Prices={Prices}
                  isLocked={isLocked}
                  isVideo={isVideo}
                  ProductImageFlags={ProductImageFlags}
                />
              )
            )
          ) : (
            <div
              className="search-listing__error"
              dangerouslySetInnerHTML={{ __html: errorTemplate }}
            />
          )}
        </div>
        <div className="search-listing__footer">
          <Select
            label={sortByLabel}
            options={sortByData}
            value={sortBy}
            onChange={(value) => setSelectValue(setSortBy, value)}
          />
          <Select
            className="sm-only"
            label={itemsPerPageLabel}
            options={itemsPerPageData}
            value={itemsPerPage}
            onChange={(value) => setSelectValue(setItemsPerPage, value)}
          />
          {data?.TotalPages ? (
            <Pagination
              currentPage={page || '1'}
              totalPages={`${data?.TotalPages}`}
              onPageChange={handlePageChange}
            />
          ) : null}
          <Select
            className="lg-only"
            label={itemsPerPageLabel}
            options={itemsPerPageData}
            value={itemsPerPage}
            onChange={(value) => setSelectValue(setItemsPerPage, value)}
          />
        </div>
      </div>
    </>
  );
}

SearchListing.propTypes = {
  endpoint: PropTypes.string.isRequired,
  pageQueryString: PropTypes.string.isRequired,
  itemsPerPageQueryString: PropTypes.string.isRequired,
  itemsPerPageLabel: PropTypes.string.isRequired,
  itemsPerPageOptions: PropTypes.string.isRequired,
  sortByLabel: PropTypes.string.isRequired,
  sortByOptions: PropTypes.string.isRequired,
  sortByQueryString: PropTypes.string.isRequired,
  appliedFiltersLabel: PropTypes.string.isRequired,
  clearAllLabel: PropTypes.string.isRequired,
  queryLabel: PropTypes.string.isRequired,
  errorTemplate: PropTypes.string.isRequired
};

export default SearchListing;
